import { axiosInstance } from './apiConfig';

/**
 * Response of POST /coach/token (ai-coach-service). The backend signs an
 * assertion with the consumer private key and exchanges it for a short-lived,
 * narrowly scoped EverPro Agents Token (EPAT). It also returns the gateway URL
 * and agent id so the browser can call the gateway directly without extra env.
 */
export interface CoachToken {
  token: string;
  // Epoch millis at which we should re-mint (already includes the server skew).
  expiresAtMs: number;
  gatewayUrl: string;
  // Conversation collection endpoint on the gateway. Append `/:conversationId`
  // for a single conversation's message history.
  conversationsUrl: string;
  agentId: string;
}

/**
 * The only `titleState` that means the gateway is done naming a conversation.
 * Anything else (it also reports in-progress and healed states) means the title
 * is not final yet and must not be treated as the conversation's name.
 */
export const COACH_TITLE_STATE_READY = 'ready';

/**
 * One past conversation as listed by the gateway. Metadata only - no message
 * content is returned by the list endpoint.
 */
export interface CoachConversationSummary {
  conversationId: string;
  startedAt: string;
  lastMessageAt: string;
  turnCount: number;
  /**
   * The name the gateway generated for the conversation. Generated
   * asynchronously, so it is absent (or not yet final) for a conversation that
   * has only just started - always go through `readyCoachConversationTitle`
   * rather than reading this directly.
   */
  title?: string;
  // How the gateway arrived at the title, e.g. 'healing-sweep' for one filled in
  // by its backfill job. Informational; we do not branch on it.
  titleSource?: string;
  // Typed as a string rather than a union: `ready` is the only value we act on,
  // and the rest of the set is the gateway's to define.
  titleState?: string;
}

/**
 * The gateway's title for a conversation, but only once it is final.
 *
 * Returns undefined while the title is still being generated, and for the
 * placeholder-ish empty case, so callers cannot accidentally persist or display
 * a half-finished name.
 */
export const readyCoachConversationTitle = (
  summary?: CoachConversationSummary,
): string | undefined => {
  if (!summary || summary.titleState !== COACH_TITLE_STATE_READY) {
    return undefined;
  }
  return summary.title?.trim() || undefined;
};

/**
 * One turn (a single user or assistant message) of a conversation's history.
 */
export interface CoachConversationTurn {
  role: string;
  // The gateway's persisted message id (`msg-...`). Namespaced differently from
  // the id the live stream reports for the same message, so it cannot be used to
  // correlate anything captured live - use runId for that.
  id: string;
  /**
   * The agent run this message belongs to (`run_...`), matching the
   * `x-ep-run-id` header captured while streaming. The user message and the
   * assistant answer of one exchange share it, which is what lets a reopened
   * conversation line its answers back up with the feedback given on them.
   */
  runId?: string;
  parts: { type: string; text?: string }[];
}

export type AgentBusinessStageCode =
  'FOUNDATIONAL' |
  'INITIAL_GROWTH' |
  'MID_GROWTH' |
  'SCALING' |
  'MATURE' |
  'ONGOING';

/**
 * ServiceNation Business Coach's registered `params.businessProfile` contract.
 * Keep this type strict: the Agents Gateway rejects unknown fields.
 */
export interface AgentBusinessProfile {
  tradeIndustry?: string;
  internalEmployeeCount?: number;
  fieldEmployeeCount?: number;
  annualRevenue?: {
    amount: number;
    currency: string;
  };
  tierCode?: string;
  businessStageCode?: AgentBusinessStageCode;
}

/**
 * Feedback rating for AI coach messages.
 */
// eslint-disable-next-line no-shadow
export enum CoachFeedbackRating {
  Like = 'like',
  Dislike = 'dislike',
}

/**
 * Request body for POST /coach/feedback.
 * Captures user feedback on a specific AI coach assistant message.
 *
 * No memberId: ai-coach-service takes it from the authenticated session, so
 * feedback can only ever be attributed to the member giving it.
 */
export interface CoachFeedbackRequest {
  conversationId: string;
  // Identifies the rated turn; what the record is keyed on. Required.
  runId: string;
  // The gateway's message id for the rated answer, which its feedback API
  // requires. Forwarded only - it is not stable across live vs history.
  assistantMessageId: string;
  agentId: string;
  rating: CoachFeedbackRating;
  text?: string;
}

/**
 * Response for POST /coach/feedback.
 */
export interface CoachFeedbackResponse {
  success: boolean;
}

/**
 * Request body for the AI Business Coach beta CTAs
 * (POST /coach/beta-notify and POST /coach/beta-join).
 */
export interface CoachBetaInterestRequest {
  memberId: string;
  email: string;
}

/**
 * Creates an EverPro Agents Token (EPAT) for the authenticated user.
 * The browser then streams from the EverPro Agents Gateway DIRECTLY with this
 * token, rather than relaying SSE through our infrastructure.
 */
export const createCoachToken = async (): Promise<CoachToken> => {
  const { data } = await axiosInstance.post<CoachToken>('/coach/token');
  return data;
};

/**
 * Everything needed to call the EverPro Agents Gateway directly from the
 * browser: the short-lived EPAT plus the endpoint and agent it is scoped to.
 */
interface CoachGatewayAccess {
  token: string;
  conversationsUrl: string;
  agentId: string;
}

/**
 * Calls a gateway conversation endpoint with the EPAT as a Bearer token.
 *
 * These go straight to the EverPro Agents Gateway rather than through
 * `axiosInstance`, for the same reason as the invoke stream: the gateway owns
 * the conversation store and the EPAT already scopes every read to the
 * authenticated member, so relaying through our own Lambda would only add a hop.
 */
const getFromGateway = async <T>(url: string, token: string): Promise<T> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Coach gateway request failed with status ${response.status}`);
  }

  return response.json() as Promise<T>;
};

/**
 * Lists the authenticated member's past coach conversations, most recently
 * active first.
 *
 * Served by our backend, which merges the gateway's list with the member's own
 * records (SN-825): deleted chats are already gone and renamed ones carry their
 * new title, so nothing deleted ever reaches the browser.
 */
export const listCoachConversations = async (): Promise<CoachConversationSummary[]> => {
  const { data } = await axiosInstance.get<CoachConversationSummary[]>('/coach/conversations');
  return data;
};

/**
 * Fetches every turn of one conversation, in chronological order.
 */
export const fetchCoachConversation = async (
  { token, conversationsUrl, agentId }: CoachGatewayAccess,
  conversationId: string,
): Promise<CoachConversationTurn[]> => {
  const url = `${conversationsUrl}/${encodeURIComponent(conversationId)}` +
    `?agentId=${encodeURIComponent(agentId)}`;
  const data = await getFromGateway<{ turns?: CoachConversationTurn[] }>(url, token);
  return data.turns ?? [];
};

/**
 * Submits user feedback for a specific AI coach assistant message.
 */
export const submitCoachFeedback = async (
  request: CoachFeedbackRequest,
): Promise<CoachFeedbackResponse> => {
  const { data } = await axiosInstance.post<CoachFeedbackResponse>('/coach/feedback', request);
  return data;
};

/**
 * One previously recorded feedback entry, as stored by ai-coach-service.
 */
export interface CoachFeedbackRecord {
  conversationId: string;
  runId: string;
  assistantMessageId: string;
  rating: CoachFeedbackRating;
}

/**
 * Lists the feedback this member already gave in a conversation. Used when a
 * past conversation is reopened so its rated messages come back showing the
 * "thank you" state instead of live thumbs that would be rejected as duplicates.
 *
 * No memberId: the service scopes the read to the authenticated member, so one
 * member can never see another's ratings.
 */
export const listCoachFeedback = async (
  conversationId: string,
): Promise<CoachFeedbackRecord[]> => {
  const { data } = await axiosInstance.get<CoachFeedbackRecord[]>('/coach/feedback', {
    params: { conversationId },
  });
  return data;
};

/**
 * Request body for POST /coach/conversations.
 *
 * No memberId: ai-coach-service takes it from the authenticated session, so a
 * member can only ever write into their own conversation index.
 */
export interface CoachConversationUpsertRequest {
  conversationId: string;
  agentId: string;
  // The gateway's generated title, sent once it is ready. Seeded server-side, so
  // sending it again is harmless and never overwrites a rename.
  title?: string;
  // Both mirror the gateway's own timestamps and are sent when we are syncing
  // against its list. Omitted on a per-turn write, which means "now".
  startedAt?: string;
  lastActiveAt?: string;
}

/**
 * One conversation record as stored by ai-coach-service. Metadata only — the
 * transcript stays in the gateway and is read back with `fetchCoachConversation`.
 */
export interface CoachConversationRecord {
  memberId: string;
  conversationId: string;
  agentId: string;
  // Absent until the gateway's generated title has been synced across, so an
  // untitled record is a normal state rather than an error.
  title?: string;
  startedAt: string;
  lastActiveAt: string;
  deleted: boolean;
  deletedAt?: string;
}

/**
 * Records (or touches) a conversation in our own store, so we own the metadata
 * the gateway does not — a member-editable title and a deleted flag (SN-828).
 *
 * Deliberately fire-and-forget at the call site: this is bookkeeping alongside
 * the real conversation, and a failure here must never break a chat turn.
 */
export const upsertCoachConversation = async (
  request: CoachConversationUpsertRequest,
): Promise<CoachConversationRecord> => {
  const { data } = await axiosInstance.post<CoachConversationRecord>(
    '/coach/conversations',
    request,
  );
  return data;
};

/**
 * Renames one of the member's conversations. Our backend renames it on the
 * gateway and in our own store; the member's title replaces the generated one
 * for good.
 */
export const renameCoachConversation = async (
  conversationId: string,
  title: string,
): Promise<CoachConversationRecord> => {
  const { data } = await axiosInstance.put<CoachConversationRecord>(
    `/coach/conversations/${encodeURIComponent(conversationId)}`,
    { title },
  );
  return data;
};

/**
 * Soft-deletes one of the member's conversations. Our backend deletes it on the
 * gateway first, which removes it from every gateway endpoint at once, then
 * flags it in our own store.
 */
export const deleteCoachConversation = async (
  conversationId: string,
): Promise<CoachConversationRecord> => {
  const { data } = await axiosInstance.delete<CoachConversationRecord>(
    `/coach/conversations/${encodeURIComponent(conversationId)}`,
  );
  return data;
};

/**
 * Records the member's request to be notified about the AI Business Coach beta
 * (persisted to DynamoDB). Fired by the "Notify me" coming-soon card CTA.
 */
export const notifyBetaCoach = async (
  request: CoachBetaInterestRequest,
): Promise<void> => {
  await axiosInstance.post('/coach/beta-notify', request);
};

/**
 * Notifies the team (via event-service → SendGrid) that a member joined the AI
 * Business Coach beta through the "Join beta" CTA.
 */
export const joinBetaCoach = async (
  request: CoachBetaInterestRequest,
): Promise<void> => {
  await axiosInstance.post('/coach/beta-join', request);
};
